import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText, verifyCrossLinks } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useVibesites,
} from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  dedupeBedroomWord,
  expandBakedBedroomRanges,
  formatBedroomsLabel,
} from '../utils/formatBedroomsLabel';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Floor Plans page hero. Keeps the `floor-plans-hero.*` keys the shared
 * PageHero instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Floor Plans</p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Floor Plans banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'An attractive apartment interior photo for the Floor Plans page — a kitchen, living space, or bedroom. Prefer staged or furnished interiors when the property has them. Alt text describes the chosen photo.',
    context:
      'Full-bleed inner page hero banner behind the "Floor Plans" title, under a dark overlay; crops to a wide landscape strip. Must be sharp, well lit and uncluttered — never a floor plan drawing, map, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Amenities, Gallery, Neighborhood, Virtual Tour, Contact); Virtual Tour also shows an interior, so pick a different room or shot. Reuse a photo only if no unused interior fits.',
  },
};

/**
 * Formats property_bedrooms_range into listed options ("Studio, 1 & 2
 * Bedroom") at resolve time, including dash ranges baked into saved copy.
 */
function FloorPlansHeroSubtitle() {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken('floor-plans-hero.subtitle');

  const patchedProperty = useMemo(() => {
    const base = (property ?? {}) as Record<string, unknown>;
    const rawBeds = base.property_bedrooms_range;
    return {
      ...base,
      property_bedrooms_range: formatBedroomsLabel(typeof rawBeds === 'string' ? rawBeds : null),
    };
  }, [property]);

  const resolvedHtml = useMemo(() => {
    if (!rawHtml) return '';
    const withFields = resolveRichText(rawHtml, patchedProperty);
    const withExpandedBeds = dedupeBedroomWord(expandBakedBedroomRanges(withFields));
    const verified = verifyCrossLinks(withExpandedBeds, pagePaths);
    const match = verified.match(/^<p>([\s\S]*)<\/p>$/);
    if (match && !/<(?:p|div|h[1-6])[\s>]/i.test(match[1])) {
      return match[1];
    }
    return verified;
  }, [rawHtml, patchedProperty, pagePaths]);

  return (
    <EditableToken
      path="floor-plans-hero.subtitle"
      type="richtext"
      label="Subtitle"
      as="h2"
      className={INNER_HERO_SUBTITLE_CLASS}
    >
      {/* RichTextContent can't format property_bedrooms_range; resolve manually. */}
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: resolvedHtml }}
        style={{ display: 'contents' }}
      />
    </EditableToken>
  );
}

export function FloorPlansHero() {
  const bg = useInnerHeroImage('floor-plans-hero.bg_image', bgImageDef, 'floor-plans', {
    altText: 'Floor Plans banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="floor-plans-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <FloorPlansHeroSubtitle />
      <RichTextContent
        path="floor-plans-hero.title"
        label="Title"
        defaultValue={TITLE}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floor-plans-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartment Floor Plans &amp; Current Availability</p>',
      generator: {
        prompt:
          'List each distinct bedroom option this property actually offers across its floor plans (e.g. "Studio, 1, 2 & 3 Bedroom"), in ascending order, followed by "Apartment Floor Plans & Current Availability". Include Studio only if a floor plan has 0 beds. Only list bedroom counts present in the floor plan data; if there is no floor plan data, write "Apartment Floor Plans & Current Availability" alone.',
        context:
          'Small eyebrow line above the "Floor Plans" H1 in the Floor Plans page hero. Plain single line — no links, lists, or emphasis.',
        length: 'one short line, under 12 words',
      },
    },
    'floor-plans-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Floor Plans</p>',
    },
  },
  slotGroups: {
    'floor-plans-hero.bg_image': bgImageDef,
  },
};

(FloorPlansHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansHero;
